import {Component, ElementRef, forwardRef, HostListener, Input, ViewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {noop} from '@angular/core/src/linker/view_utils';
import {AuthService} from '../../../security/authorization/auth.service';
import {Roles} from '../../../../domain/authorization/roles';
import {MonthSelectorComponentModel} from './selector/month-selector-model.class';
import {BehaviorSubject, Observable, ReplaySubject} from 'rxjs';
import moment = require('moment');
import Moment = moment.Moment;
import isString = require('lodash/isString');
import {coerceBooleanProperty} from '../../../../common/value/coercion/boolean-coercion.util';

// region NG_VALUE_ACCESSOR
const MY_MONTH_PICKER_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MonthPickerComponent),
    multi: true
};
// endregion

const DATE_REGEX = /^(\d{2})\/(\d{4})$/;
const DATE_MASK = [/\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/];

let nextUniqueId = 0;

/**
 * Created by Rodrigo Quesada on 10/12/16.
 */

// region Other Classes
export interface MonthPickerComponentModelData {
    date?: Moment;
    minDate: Moment;
    maxDate: Moment;
}

export class MonthPickerComponentModel extends MonthSelectorComponentModel {
    constructor(data: MonthPickerComponentModelData) {
        const selectorData = {
            ...data,
            ...{date: data.date ? data.date : undefined}
        };
        super(selectorData);
    }
}
// endregion

@Component({
    selector: 'my-month-picker',
    template: require('./month-picker.component.html'),
    styles: [require('./month-picker.component.scss')],
    providers: [MY_MONTH_PICKER_CONTROL_VALUE_ACCESSOR]
})
export class MonthPickerComponent implements ControlValueAccessor {

    readonly id = `my-month-picker-${++nextUniqueId}`;

    // region Input
    @Input() placeholder: string;
    @Input() name: string | null = null;
    @Input() errorMessage: String;
    @Input() errorMessagePosition: String = 'right';

    @Input() disabled: boolean = false;

    model$ = new ReplaySubject<MonthPickerComponentModel>(1);

    @Input() set model(value: MonthPickerComponentModel) {
        this.model$.next(value);
    }

    // endregion

    // region Children
    @ViewChild('input') private _inputElement: ElementRef;
    // endregion

    readonly value$ = new BehaviorSubject<Moment | undefined>(undefined);

    private set value(value: Moment | undefined) { this.value$.next(value); }

    readonly stringValue$: Observable<string> = this.value$.map(value => value ? value.format('MM/YYYY') : '')
    //fix for TextMask not clearing input field completely when set to empty string
        .do(value => { if (value === '') this._inputElement.nativeElement.value = value; });

    changeValue(value: string | Moment | undefined) {
        const newValue: Moment | undefined = isString(value) ? parseValue(value) : value;

        if (newValue === undefined && this.value$.getValue() !== newValue
            || newValue !== undefined && newValue.isValid()) {

            this.model$.first().subscribe(model => model.dateSelection = newValue);
        }
    }

    displaySelector: boolean = false;

    private focused: boolean = false;

    private _readonly: boolean = true;

    @Input()
    get readonly(): boolean {
        return this._readonly;
    }

    set readonly(value) {
        this._readonly = coerceBooleanProperty(value);
    }

    get datePattern() {
        return DATE_REGEX;
    }

    get dateMask() {
        return DATE_MASK;
    }

    get empty() {
        return this._inputElement.nativeElement.value === '';
    }

    constructor(private _el: ElementRef, private _authService: AuthService) {
        this.model$.switchMap(model => model.date$).subscribe(dateChange => {
            const date = dateChange.value;
            this.value = date;
            if (dateChange.selected) this._onChangeCallback(date);
        });
        this._authService.user$.take(1).subscribe(user => {
            if (user && user.hasAnyRole([Roles.ADMIN])) {
                this._readonly = false;
            }
        });
    }

    // region Events
    onFocus() {
        this.focused = true;
        this.displaySelector = true;
    }

    onBlur() {
        this.focused = false;
        this._onTouchedCallback();
    }

    clearDate() {
        this.changeValue(undefined);
    }

    clickOutside(event) {
        this.displaySelector = false;
    }

    @HostListener('keydown.tab', ['$event'])
    onTab(event: KeyboardEvent) {
        event.preventDefault();
        this.displaySelector = false;
        this.nextInputElement().focus();
    }

    private nextInputElement() {
        return $(this._el.nativeElement)
            .nextAll()
            .map((i, s) => $(s).find('input').first()[0])
            .first();
    }

    // endregion

    // region ControlValueAccessor
    /** Callback registered via registerOnTouched (ControlValueAccessor) */
    private _onTouchedCallback: () => void = noop;
    /** Callback registered via registerOnChange (ControlValueAccessor) */
    private _onChangeCallback: (_: any) => void = noop;

    writeValue(value: Moment | null | undefined): void {
        const newValue = value ? value : undefined;
        this.model$.first().subscribe(model => model.date = newValue);
    }

    registerOnChange(fn: any): void {
        this._onChangeCallback = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouchedCallback = fn;
    }

    // endregion
}

function parseValue(value: string): Moment | undefined {
    let match = DATE_REGEX.exec(value);
    return match ? moment({
        month: +match[1] - 1,
        year: +match[2]
    }) : undefined;
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/components/forms/controls/monthpicker/month-picker.component.ts